import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { PHYSICAL_LOAN_ITEM_OUTCOMES, type LoanAgreement } from "@/contracts";

import { recordReturnMutation } from "@/api/queries";
import { Alert } from "@/components/ui/alert";
import { SelectField, TextAreaField, TextField } from "@/components/ui/field";
import { FormDialog } from "@/components/ui/form-dialog";
import { useApiClient } from "@/lib/api-context";
import { formatDevEui } from "@/lib/format";
import { LOAN_OUTCOME_LABELS } from "./loan-labels";

type FormValues = {
  deviceId: string;
  returnedOn: string;
  outcome: (typeof PHYSICAL_LOAN_ITEM_OUTCOMES)[number];
  notes: string;
};

/**
 * Accounting for one unit.
 *
 * One at a time, because that is how equipment comes back: three units
 * collected and the fourth missing is the normal case. The outcome matters
 * more than it looks — under article 1536(d) of the Código Civil y Comercial
 * the borrower answers for a unit that is lost or damaged even by chance, so
 * "lost" is the entry that has a cost attached to it.
 */
export function RecordReturnDialog({
  agreement,
  onClose,
}: {
  agreement: LoanAgreement;
  onClose: () => void;
}) {
  const { t } = useTranslation("loans");
  const client = useApiClient();
  const queryClient = useQueryClient();

  const formSchema = z.object({
    deviceId: z.string().min(1, t("chooseUnit")),
    returnedOn: z.string().min(1, t("enterDate")),
    outcome: z.enum(PHYSICAL_LOAN_ITEM_OUTCOMES),
    notes: z.string().trim().max(500, t("notesTooLong")),
  });

  const outstanding = agreement.items.filter((item) => item.outcome === null);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      deviceId: outstanding[0]?.deviceId ?? "",
      returnedOn: new Date().toISOString().slice(0, 10),
      outcome: "RETURNED",
      notes: "",
    },
  });

  const deviceId = useWatch({ control, name: "deviceId" });
  const outcome = useWatch({ control, name: "outcome" });

  const mutation = useMutation({
    ...recordReturnMutation(client),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["loan-agreements"] });
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    await mutation.mutateAsync({
      id: agreement.id,
      body: {
        deviceId: values.deviceId,
        returnedOn: values.returnedOn,
        outcome: values.outcome,
        ...(values.notes ? { notes: values.notes } : {}),
      },
    });

    onClose();
  });

  const error = (field: keyof FormValues) => {
    const message = errors[field]?.message;

    return message === undefined ? {} : { error: message };
  };

  return (
    <FormDialog
      open
      title={t("returnDialogTitle")}
      description={t("returnDialogDescription", {
        number: agreement.number,
        count: outstanding.length,
      })}
      submitLabel={t("returnSubmit")}
      pending={isSubmitting || mutation.isPending}
      onSubmit={(event) => void onSubmit(event)}
      onCancel={onClose}
    >
      <div className="flex flex-col gap-4">
        {mutation.isError ? <Alert>{mutation.error.message}</Alert> : null}

        <SelectField
          label={t("returnUnit")}
          required
          {...error("deviceId")}
          value={deviceId}
          onChange={(value) =>
            setValue("deviceId", value, { shouldValidate: true, shouldDirty: true })
          }
          data={outstanding.map((item) => ({
            value: item.deviceId,
            label: `${formatDevEui(item.devEui)} — ${item.manufacturer} ${item.model}`,
          }))}
        />

        <SelectField
          label={t("returnOutcome")}
          required
          value={outcome}
          onChange={(value) =>
            setValue("outcome", value as FormValues["outcome"], {
              shouldValidate: true,
              shouldDirty: true,
            })
          }
          data={PHYSICAL_LOAN_ITEM_OUTCOMES.map((value) => ({
            value,
            label: LOAN_OUTCOME_LABELS[value],
          }))}
        />

        <TextField
          label={t("returnDate")}
          type="date"
          hint={t("returnDateHint")}
          required
          {...error("returnedOn")}
          {...register("returnedOn")}
        />

        <TextAreaField
          label={t("returnNote")}
          hint={t("returnNoteHint")}
          {...error("notes")}
          {...register("notes")}
        />

        <Alert tone="info" title={t("returnWhyTitle")} announce={false}>
          {t("returnWhyBody")}
        </Alert>
      </div>
    </FormDialog>
  );
}
